"use client";

import React, { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import { Float, Box, MeshDistortMaterial, Instances, Instance, Environment } from '@react-three/drei';
import * as THREE from 'three';
import ConditionalCanvas from "./ConditionalCanvas";

function DataBlock({ position, speed, color }: { position: [number, number, number], speed: number, color: string }) {
  const mesh = useRef<THREE.Mesh>(null!);
  
  useFrame((state) => {
    const time = state.clock.getElapsedTime();
    mesh.current.position.y += Math.sin(time * speed) * 0.01;
    mesh.current.rotation.x = time * speed * 0.2;
    mesh.current.rotation.z = time * speed * 0.1;
  });

  return (
    <Float speed={speed * 2} rotationIntensity={1} floatIntensity={1}>
      <Box ref={mesh} position={position} args={[1, 1, 1]}>
        <MeshDistortMaterial
          color={color}
          speed={speed}
          distort={0.2}
          radius={1}
          emissive={color}
          emissiveIntensity={0.2}
          transparent
          opacity={0.6}
        />
      </Box>
    </Float>
  );
}

function DataStream({ count = 15 }) {
  const blocks = useMemo(() => {
    return Array.from({ length: count }).map((_, i) => ({
      position: [
        (Math.random() - 0.5) * 20,
        (Math.random() - 0.5) * 10,
        (Math.random() - 0.5) * 10,
      ] as [number, number, number],
      speed: 0.5 + Math.random(),
      color: i % 2 === 0 ? "#00f3ff" : "#0066FF",
    }));
  }, [count]);

  return (
    <>
      {blocks.map((block, i) => (
        <DataBlock key={i} {...block} />
      ))}
    </>
  );
}

function Grid() {
  return (
    <gridHelper args={[50, 50, "#00f3ff", "#0a192f"]} rotation={[Math.PI / 2, 0, 0]} position={[0, 0, -10]} />
  );
}

export default function NewsHeroScene() {
  return (
    <div className="absolute inset-0 z-0 bg-[#040d21]">
      <ConditionalCanvas camera={{ position: [0, 0, 15], fov: 45 }}>
        <ambientLight intensity={0.4} />
        <pointLight position={[10, 10, 10]} intensity={1} color="#00f3ff" />
        <pointLight position={[-10, -10, -10]} intensity={0.5} color="#0066FF" />
        
        <DataStream count={20} />
        <Grid />
        
        <fog attach="fog" args={['#040d21', 10, 25]} />
      </ConditionalCanvas>
      
      {/* Overlay gradient to ensure text readability */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#040d21] via-transparent to-[#040d21]/60 pointer-events-none" />
    </div>
  );
}
